// src/features/home/components/ProjectCard.tsx

import React, { useRef } from 'react';
import styles from './ProjectCard.module.scss';

interface ProjectCardProps {
  title: string;
  description: string;
  image: string;
  technologies: string[];
  projectUrl?: string; // optional
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  title,
  description,
  image,
  technologies,
  projectUrl,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    cardRef.current.style.setProperty('--mouse-x', `${x}px`);
    cardRef.current.style.setProperty('--mouse-y', `${y}px`);
  };

  const cardContent = (
    <>
      <div className={styles.hoverOverlay}></div>
      <div className={styles.imageWrapper}>
        <img src={image} alt={title} />
      </div>
      <div className={styles.content}>
        <h3 className={styles.title}>
          {projectUrl ? (
            <a
              href={projectUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.projectLink}
              aria-label={`${title} (opens in a new tab)`}
            >
              <span>
                {title}
                <svg
                  className={styles.externalLinkIcon}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5A.75.75 0 0013.25 5h-7.5a.75.75 0 000 1.5h5.69L4.22 13.72a.75.75 0 000 1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            </a>
          ) : (
            <span className={styles.projectTitle}>
              {title}
              <svg
                className={styles.externalLinkIcon}
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5A.75.75 0 0013.25 5h-7.5a.75.75 0 000 1.5h5.69L4.22 13.72a.75.75 0 000 1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          )}
        </h3>
        <p className={styles.description}>{description}</p>
        <ul className={styles.techList} aria-label="Technologies used">
          {technologies.map((tech, index) => (
            <li key={index} className={styles.techItem}>
              <div className={styles.techBadge}>{tech}</div>
            </li>
          ))}
        </ul>
      </div>
    </>
  );

  return (
    <li className={styles.projectItem}>
      {projectUrl ? (
        <a
          href={projectUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.projectCard}
          aria-label={`${title} (opens in a new tab)`}
        >
          {cardContent}
        </a>
      ) : (
        <div 
          ref={cardRef}
          onMouseMove={handleMouseMove}
          className={`${styles.projectCard} ${styles.spotlightCard}`}
        >
          {cardContent}
        </div>
      )}
    </li>
  );
};

export default ProjectCard;